import React, { useRef, useState, useEffect } from 'react';
import { RecoilState, useRecoilValue, useRecoilState, useResetRecoilState } from 'recoil';
import { SortingRule } from 'react-table';
import {
  AttentionMessage,
  Box,
  DataTableColumns,
  DataTable,
  TableState,
} from '@tq/ui-components';

import { DataTablePagination } from 'components';
import { PaginationInfo, Team } from 'types';
import { useTranslation } from 'react-i18next';
import { dataTableStyle } from '../../styles';

export type TeamTableProps = {
  teams: Team[];
  columns: Array<DataTableColumns<Team>>;
  pagination: PaginationInfo | undefined;
  pageNumberState: RecoilState<number>;
  pageSizeState: RecoilState<number>;
  sortByColumnState: RecoilState<string>;
  sortByDescendingState: RecoilState<boolean | undefined>;
  editingState: RecoilState<number | undefined>;
};

export const TeamTable: React.FC<TeamTableProps> = ({
  teams,
  columns,
  pagination,
  pageNumberState,
  pageSizeState,
  sortByColumnState,
  sortByDescendingState,
  editingState,
}) => {
  const { t } = useTranslation('manage_organisation');

  const tableRef = useRef(null);
  const resetEditingState = useResetRecoilState(editingState);
  const pageSize = useRecoilValue(pageSizeState);
  const pageNumber = useRecoilValue(pageNumberState);

  const [orderByDescending, setOrderBy] = useRecoilState(sortByDescendingState);
  const [orderByValue, setOrderByValue] = useRecoilState(sortByColumnState);
  const [sortBy, setSortBy] = useState<SortingRule<Team>[]>([
    {
      id: orderByValue,
      desc: orderByDescending,
    },
  ]);

  const handleStateChange = (state: TableState<Team>): void => {
    resetEditingState();
    if (!state.filters.length && !state.sortBy.length) return;
    const newOrderBy = state.sortBy?.[0]?.desc;
    const newOrderByValue = state.sortBy?.[0]?.id;
    if (state.sortBy !== sortBy) {
      setSortBy(state.sortBy);
    }
    if (newOrderByValue) {
      setOrderBy(newOrderBy);
      setOrderByValue(newOrderByValue);
    }
  };

  useEffect(() => {
    resetEditingState();
  }, [pageSize, pageNumber, resetEditingState]);

  return (
    <Box sx={dataTableStyle}>
      <DataTable
        id='teamTable'
        columns={columns}
        data={teams}
        disableSortRemove={true}
        disablePagination={true}
        enableOverflowScrollingContainer={true}
        onStateChange={handleStateChange}
        size='sm'
        whiteSpace='normal'
        sortBy={sortBy}
        manualSortBy={true}
        style={{ tableLayout: 'fixed' }}
      />
      {teams.length === 0 && (
        <Box padding={5}>
          <AttentionMessage
            width='100%'
            status={'info'}
            title={t('teams_no_data', 'No Data')}
            variant='inline'
          />
        </Box>
      )}
      <DataTablePagination
        paginationInfo={pagination}
        pageNumberState={pageNumberState}
        pageSizeState={pageSizeState}
        tableRef={tableRef}
        tableScrollHeaderOffset={167}
      />
    </Box>
  );
};
